/**
 * 📧 EMAIL VERIFICATION MODAL
 * 
 * Modal de verificação de email no cadastro
 * Design: Consistente com TwoFactorModal (bonito, moderno, gradientes)
 * 
 * Features:
 * - Input de 6 dígitos
 * - Countdown de expiração (10 minutos)
 * - Botão "Reenviar código"
 * - Feedback visual (sucesso/erro)
 * - Tentativas restantes
 * 
 * @component
 */

import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import TwoFactorInput from '../auth/TwoFactorInput';
import './EmailVerificationModal.scss';

const EmailVerificationModal = ({ 
  show, 
  onHide, 
  onVerify, 
  onResend,
  email,
  name,
  expiresIn = 600 // 10 minutos em segundos
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [timeLeft, setTimeLeft] = useState(expiresIn);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  
  const intervalRef = useRef(null);

  // Countdown timer
  useEffect(() => {
    if (!show) return;

    setTimeLeft(expiresIn);
    setCanResend(false);

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [show, expiresIn]);

  /**
   * Formatar tempo restante (MM:SS)
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Handler de verificação de código
   */
  const handleVerify = async (submittedCode) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const result = await onVerify(submittedCode || code);

      if (result.success) {
        setSuccess(true);
        setError('');
        
        // Aguardar 1s para mostrar feedback e fechar modal
        setTimeout(() => {
          onHide();
          setCode('');
        }, 1000);
      } else {
        setError(result.message || 'Código inválido');
        setCode('');
        
        if (result.attemptsLeft !== undefined) {
          setAttemptsLeft(result.attemptsLeft);
        }
      }
    } catch (err) {
      setError('Erro ao verificar código. Tente novamente.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handler de reenvio de código
   */
  const handleResend = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await onResend();

      if (result.success) {
        setTimeLeft(result.expiresIn || 600);
        setCanResend(false);
        setAttemptsLeft(3);
        setCode('');
        setError('');
        
        // Reiniciar countdown
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(intervalRef.current);
              setCanResend(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(result.message || 'Erro ao reenviar código');
      }
    } catch (err) {
      setError('Erro ao reenviar código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mascara email para exibição
   */
  const maskEmail = (email) => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 2) {
      return `${localPart[0]}***@${domain}`;
    }
    const visibleStart = localPart.substring(0, 1);
    const visibleEnd = localPart.substring(localPart.length - 1);
    return `${visibleStart}***${visibleEnd}@${domain}`;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      className="email-verification-modal"
    >
      <div className="email-verification-modal__container">
        {/* Header com gradiente */}
        <div className="email-verification-modal__header">
          <div className="email-verification-modal__icon">
            <Icon icon="mdi:email-check" />
          </div>
          <h3 className="email-verification-modal__title">Verifique seu Email</h3>
          <p className="email-verification-modal__subtitle">
            Digite o código enviado para<br />
            <strong>{maskEmail(email)}</strong>
          </p>
        </div>

        {/* Corpo */}
        <div className="email-verification-modal__body">
          {/* Input de 6 dígitos */}
          <TwoFactorInput
            value={code}
            onChange={setCode}
            error={!!error}
            disabled={loading || success}
          />

          {/* Countdown */}
          {!canResend && timeLeft > 0 && (
            <div className="email-verification-modal__countdown">
              <Icon icon="mdi:clock-outline" />
              <span>Código expira em <strong>{formatTime(timeLeft)}</strong></span>
            </div>
          )}

          {/* Tentativas restantes */}
          {attemptsLeft < 3 && attemptsLeft > 0 && !success && (
            <div className="email-verification-modal__attempts">
              <Icon icon="mdi:alert-circle-outline" />
              <span>{attemptsLeft} tentativa(s) restante(s)</span>
            </div>
          )}

          {/* Feedback de sucesso */}
          {success && (
            <div className="email-verification-modal__success">
              <Icon icon="mdi:check-circle" />
              <span>Email verificado com sucesso!</span>
            </div>
          )}

          {/* Feedback de erro */}
          {error && (
            <div className="email-verification-modal__error">
              <Icon icon="mdi:alert-circle" />
              <span>{error}</span>
            </div>
          )}

          {/* Ação principal / Reenvio */}
          {canResend ? (
            <button
              onClick={handleResend}
              disabled={loading}
              className="email-verification-modal__resend"
            >
              <Icon icon="mdi:email-sync" />
              Reenviar código
            </button>
          ) : (
            <button
              onClick={() => handleVerify(code)}
              disabled={loading || !/^\d{6}$/.test(code)}
              className="email-verification-modal__resend"
            >
              <Icon icon="mdi:login" />
              Acessar
            </button>
          )}

          {/* Link voltar */}
          <button
            onClick={onHide}
            className="email-verification-modal__back"
            disabled={loading}
          >
            <Icon icon="mdi:arrow-left" />
            Voltar ao cadastro
          </button>

          {/* Aviso sobre delay - Footer */}
          <div className="email-verification-modal__info-box">
            <Icon icon="mdi:information-outline" width="16" />
            <span>O email pode levar até 2 minutos para chegar. Caso não chegue, verifique sua caixa de spam.</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EmailVerificationModal;

